import { useState, useCallback } from 'react';

/**
 * Detecta si la página está embebida dentro de un <iframe>.
 * Si la política cross-origin bloquea el acceso a window.top,
 * asumimos que estamos en un iframe de origen cruzado.
 */
function isInIframe() {
  try {
    return window.self !== window.top;
  } catch {
    return true; // acceso denegado por cross-origin = definitivamente en iframe
  }
}

/**
 * Devuelve true solo si el browser soporta navigator.share
 * Y la página NO está embebida en un iframe.
 */
function canUseNativeShare() {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.share === 'function' &&
    !isInIframe()
  );
}

/**
 * Hook para compartir URLs de forma robusta en cualquier contexto.
 *
 * Uso:
 *   const { share, panelOpen, closePanel } = useShare(url, title);
 *   // En el JSX: <button onClick={share}>Compartir</button>
 *   //            <SharePanel isOpen={panelOpen} onClose={closePanel} url={url} title={title} />
 *
 * Comportamiento:
 *   - Ventana normal + soporte nativo → navigator.share()
 *   - Iframe OR sin soporte nativo → abre panel interno con opciones de copia/sociales
 */
export function useShare(url, title) {
  const [panelOpen, setPanelOpen] = useState(false);

  const share = useCallback(
    async (e) => {
      if (e?.preventDefault) e.preventDefault();
      if (e?.stopPropagation) e.stopPropagation();

      if (canUseNativeShare()) {
        try {
          await navigator.share({ title, url });
        } catch {
          // El usuario canceló — no hacer nada
        }
      } else {
        setPanelOpen((prev) => !prev);
      }
    },
    [url, title]
  );

  const closePanel = useCallback(() => setPanelOpen(false), []);

  return { share, panelOpen, closePanel };
}
